import { ref } from 'vue';
import type { Rule } from 'ant-design-vue/es/form';

export interface ContractData {
  projectName: string;
  contractId: string;
  contractName: string;
  officeInfo: string;
  office: string;
  address: string;
  telephone: string;
  email: string;
  dischargeSitename: string;
  contractPeriodFrom: string;
  contractPeriodTo: string;
  paymentMethod: string;
}

export const formData = ref<ContractData>({
  projectName: '',
  contractId: '',
  contractName: '',
  officeInfo: '',
  office: '',
  address: '',
  telephone: '',
  email: '',
  dischargeSitename: '',
  contractPeriodFrom: '',
  contractPeriodTo: '',
  paymentMethod: ''
});

export interface ContractDataBoolean {
  projectName: boolean;
  contractId: boolean;
  contractName: boolean;
  officeInfo: boolean;
  office: boolean;
  address: boolean;
  telephone: boolean;
  email: boolean;
  dischargeSitename: boolean;
  contractPeriodFrom: boolean;
  contractPeriodTo: boolean;
  paymentMethod: boolean;
  contractType: boolean;
}

export const requiredDataConfirm: ContractDataBoolean = {
  projectName: false,
  contractId: false,
  contractName: false,
  officeInfo: false,
  office: false,
  address: false,
  telephone: false,
  email: false,
  dischargeSitename: false,
  contractPeriodFrom: false,
  contractPeriodTo: false,
  paymentMethod: false,
  contractType: false
};

export const requiredDataCreate: ContractDataBoolean = {
  projectName: true,
  contractId: false,
  contractName: true,
  officeInfo: false,
  office: false,
  address: false,
  telephone: false,
  email: false,
  dischargeSitename: false,
  contractPeriodFrom: true,
  contractPeriodTo: true,
  paymentMethod: true,
  contractType: true
};

export const readonlyDataConfirm: ContractDataBoolean = {
  projectName: true,
  contractId: true,
  contractName: true,
  officeInfo: true,
  office: true,
  address: true,
  telephone: true,
  email: true,
  dischargeSitename: true,
  contractPeriodFrom: true,
  contractPeriodTo: true,
  paymentMethod: true,
  contractType: true
};

export const readonlyDataCreate: ContractDataBoolean = {
  projectName: false,
  contractId: true,
  contractName: false,
  officeInfo: true,
  office: true,
  address: true,
  telephone: true,
  email: true,
  dischargeSitename: true,
  contractPeriodFrom: false,
  contractPeriodTo: false,
  paymentMethod: false,
  contractType: false
};

interface Office {
  name: string;
  address: string;
  email: string;
  tel: string;
}

interface Discharges {
  discharges: string;
  address: string;
  email: string;
  tel: string;
  posCode: string;
}

interface Company {
  id: string;
  name: string;
  address: string;
  contractPic: string;
}

interface Waste {
  id: string;
  name: string;
  wasteCode: string;
}

interface Packing {
  style: string;
}
interface PwasteIndustry {
  id: string;
  waste: Waste;
  packing: Packing;
  quantity: string;
  dimensions: string;
  frequency: string;
  measureUnit: MeasureUnit;
  frequencyNumber: Number;
}

interface Bag {
  l30: String;
  l45: String;
  l70: String;
}

interface PwasteRegular {
  id: string;
  wasteRegularType: String;
  capacity: Number;
  frequency: String[];
  bag: Bag;
}

interface MeasureUnit {
  unit: string;
}
export interface Project {
  id: string;
  projectTitle: string;
  projectStatus: string;
  office: Office;
  discharges: Discharges;
  contract: Contract;
  type: string;
  company: Company;
  transportCompany: Company;
  pwasteIndustries: PwasteIndustry[];
  pwasteRegulars: PwasteRegular[];
}

export interface PwasteIndustryShow {
  id: String;
  name: String;
  packingStyle: String;
  quantity: String;
  dimensions: String;
  frequency: String;
}

export interface PwasteRegularShow {
  id: String;
  name: String;
  capacity: Number;
  l30: String;
  l45: String;
  l70: String;
  numDay: Number;
  dayOfWeek: String;
}

interface File {
  disposal: string;
  regular: string;
  combine: string;
  collection: string;
}

export interface Contract {
  id: string;
  contractName: string;
  startDate: string;
  endDate: string;
  contractType: string;
  paymentMethod: string;
  file: File;
  licenseNumber: string;
  licenseDistrict: string;
  licenseExpireDate: string;
}

export const initProject: Project = {
  contract: {
    id: '',
    contractName: '',
    startDate: '',
    endDate: '',
    contractType: 'AUTO_UPDATE', // default value
    paymentMethod: 'PAYMENT_INVOICE', // default value,
    file: {
      disposal: '',
      regular: '',
      combine: '',
      collection: ''
    },
    licenseNumber: '',
    licenseDistrict: '',
    licenseExpireDate: ''
  },
  office: {
    name: '',
    address: '',
    email: '',
    tel: ''
  },
  discharges: {
    discharges: '',
    address: '',
    email: '',
    tel: '',
    posCode: ''
  },
  id: '',
  projectTitle: '',
  projectStatus: '',
  type: '',
  company: {
    id: '',
    name: '',
    address: '',
    contractPic: ''
  },
  transportCompany: {
    id: '',
    name: '',
    address: '',
    contractPic: ''
  },
  pwasteIndustries: [],
  pwasteRegulars: []
};

export const columnRegular = [
  {
    title: '廃棄物名',
    dataIndex: 'name',
    key: 'name',
    customHeaderCell: () => {
      return { style: { textAlign: 'center' } };
    },
    width: 400,
    customCell: () => {
      return { style: { textAlign: 'left' } };
    }
  },
  {
    title: '一ヶ月当たりの容量',
    dataIndex: 'capacity',
    key: 'capacity',
    customHeaderCell: () => {
      return { style: { textAlign: 'center' } };
    },
    width: 200,
    customCell: () => {
      return { style: { textAlign: 'right' } };
    }
  },
  {
    title: '30リットル',
    dataIndex: 'l30',
    key: 'l30',
    customHeaderCell: () => {
      return { style: { textAlign: 'center' } };
    },
    width: 200,
    customCell: () => {
      return { style: { textAlign: 'right' } };
    }
  },
  {
    title: '45リットル',
    dataIndex: 'l45',
    key: 'l45',
    customHeaderCell: () => {
      return { style: { textAlign: 'center' } };
    },
    width: 200,
    customCell: () => {
      return { style: { textAlign: 'right' } };
    }
  },
  {
    title: '70リットル',
    dataIndex: 'l70',
    key: 'l70',
    customHeaderCell: () => {
      return { style: { textAlign: 'center' } };
    },
    width: 200,
    customCell: () => {
      return { style: { textAlign: 'right' } };
    }
  },
  {
    title: '収集回数',
    dataIndex: 'numDay',
    key: 'numDay',
    customHeaderCell: () => {
      return { style: { textAlign: 'center' } };
    },
    width: 200,
    customCell: () => {
      return { style: { textAlign: 'right' } };
    }
  },
  {
    title: '曜日',
    dataIndex: 'dayOfWeek',
    key: 'dayOfWeek',
    customHeaderCell: () => {
      return { style: { textAlign: 'center' } };
    },
    width: 200,
    customCell: () => {
      return { style: { textAlign: 'left' } };
    }
  }
];

export const columnIndustry = [
  {
    title: '廃棄物名',
    dataIndex: 'name',
    key: 'name',
    width: 400,
    customHeaderCell: () => {
      return { style: { textAlign: 'center' } };
    }
  },
  {
    title: '数量',
    dataIndex: 'quantity',
    key: 'quantity',
    customHeaderCell: () => {
      return { style: { textAlign: 'center' } };
    },
    width: 200,
    customCell: () => {
      return { style: { textAlign: 'right' } };
    }
  },
  {
    title: '荷姿',
    dataIndex: 'packingStyle',
    key: 'packingStyle',
    width: 200,
    customHeaderCell: () => {
      return { style: { textAlign: 'center' } };
    },

    customCell: () => {
      return { style: { textAlign: 'left' } };
    }
  },

  {
    title: '寸法・重量',
    dataIndex: 'dimensions',
    key: 'dimensions',
    customCell: () => {
      return { style: { textAlign: 'left' } };
    },
    width: 200,
    customHeaderCell: () => {
      return { style: { textAlign: 'center' } };
    }
  },

  {
    title: '搬出頻度',
    dataIndex: 'frequency',
    key: 'frequency',
    customCell: () => {
      return { style: { textAlign: 'left' } };
    },
    width: 150,
    customHeaderCell: () => {
      return { style: { textAlign: 'center' } };
    }
  }
];

const mapDayOfWeekToJapanese = (dayOfWeek: String) => {
  if (dayOfWeek == 'mon') {
    return '月';
  }
  if (dayOfWeek == 'tue') {
    return '火';
  }
  if (dayOfWeek == 'wed') {
    return '水';
  }
  if (dayOfWeek == 'thu') {
    return '木';
  }
  if (dayOfWeek == 'fri') {
    return '金';
  }
  if (dayOfWeek == 'sat') {
    return '土';
  }
  if (dayOfWeek == 'sun') {
    return '日';
  } else {
    return '';
  }
};

export const gregorianToJapaneseEra = (dateStr: String) => {
  // Define Japanese eras with their starting Gregorian year
  if (dateStr === '' || dateStr === null) {
    return '';
  }
  const eras = [
    { name: '令和', startYear: 2019 },
    { name: '平成', startYear: 1989 },
    { name: '昭和', startYear: 1926 },
    { name: '大正', startYear: 1912 },
    { name: '明治', startYear: 1868 }
  ];

  // Parse the input date
  const [year, month, day] = dateStr.split('/').map(Number);

  // Find the appropriate era based on the Gregorian year
  for (const era of eras) {
    if (year >= era.startYear) {
      const japaneseYear = year - era.startYear + 1; // Convert to Japanese era year
      return `${era.name}${japaneseYear}年${month}月${day}日`;
    }
  }

  throw new Error('Date is before supported Japanese eras.');
};

export const scrollToField = (fieldId: string) => {
  setTimeout(() => {
    const fieldElement = document.querySelector(`#${fieldId}`) as HTMLElement;
    if (fieldElement) {
      fieldElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      fieldElement.focus();
    }
  }, 100);
};

export const mapDayOfWeek = (daysOfWeek: String[]) => {
  const frequency = daysOfWeek.map((day) => mapDayOfWeekToJapanese(day)).join('、');
  return frequency;
};
